<script lang="ts">
	import { getContext } from "svelte";
	import Modal from "./Modal.svelte";
	import Hero from "./api/Hero";
	import dayjs from "dayjs";

	export let heroes: Array<Hero> = [];
	export let showEditLinks: boolean = false;

	let currentHero: Hero = new Hero();
	let modalControls;

	const pageId = getContext("PageID");
	const moduleId = getContext("ModuleID");
	const editLink = `/SuperFlexi/Edit.aspx?pageid=${pageId}&mid=${moduleId}&itemid=`;

	const modalOptions = {
		size: "lg",
	};

	function showHero(hero: Hero) {
		currentHero = hero;
		modalControls.show();
	}
</script>

<div class="hero-list">
	{#each heroes as hero}
		<figure id="hero{hero.id}" class="hero" on:click={() => showHero(hero)}>
			{#if showEditLinks}
				<a
					href={editLink + hero.id}
					class="hero-edit-link"
					title="Edit this Hero"
					on:click|stopPropagation
				>
					<i class="fa fa-edit" />
				</a>
			{/if}

			<img
				class="hero-photo"
				src={hero.photo}
				alt={hero.name}
				on:contextmenu|preventDefault|stopPropagation
			/>

			<figcaption class="hero-caption">
				<h3 class="hero-name h4">{hero.name}</h3>

				{#if hero.descriptions?.length}
					{#each hero.descriptions as desc}
						<p class="hero-description">{desc}</p>
					{/each}
				{/if}

				{#if hero.dateOfEntry}
					<p class="hero-service-time">
						({dayjs(hero.dateOfEntry).format("MMM DD, YYYY") ||
							""}{hero.dateOfDischarge
							? " - " +
							  dayjs(hero.dateOfDischarge).format("MMM DD, YYYY")
							: " - Today"})
					</p>
				{/if}
			</figcaption>
		</figure>
	{/each}
</div>

<Modal options={modalOptions} bind:controls={modalControls}>
	<svelte:fragment slot="title">{currentHero.name}</svelte:fragment>

	<svelte:fragment slot="body">
		<div class="hero-details row form-group">
			<div class="hero-details-left col-md-4">
				<figure
					class="hero-details-photo"
					on:contextmenu|preventDefault|stopPropagation
				>
					<img
						class="hero-details-photo-image"
						src={currentHero.photo}
						alt={currentHero.name}
					/>
				</figure>
			</div>

			<div class="hero-details-right col-md-8">
				<table class="hero-details-table table table-bordered table-striped table-condensed">
					<tbody>
						<tr>
							<th class="hero-details-th">Full Name</th>
							<td>
								{currentHero.firstName || ""}
								{currentHero.middleName || ""}
								{currentHero.lastName || ""}
							</td>
						</tr>
						<tr>
							<th class="hero-details-th">Date of Birth</th>
							<td>
								{currentHero.dateOfBirth
									? dayjs(currentHero.dateOfBirth).format(
											"MMMM DD, YYYY"
									  )
									: "Unknown"}
							</td>
						</tr>
						<tr>
							<th class="hero-details-th">Date of Death</th>
							<td>
								{currentHero.dateOfDeath
									? dayjs(currentHero.dateOfDeath).format(
											"MMMM DD, YYYY"
									  )
									: "Unknown"}
							</td>
						</tr>
						<tr>
							<th class="hero-details-th">Component</th>
							<td>{currentHero.component || "Unknown"}</td>
						</tr>
						<tr>
							<th class="hero-details-th">Rank</th>
							<td>{currentHero.rank || "Unknown"}</td>
						</tr>
						<tr>
							<th class="hero-details-th">Pay Grade</th>
							<td>{currentHero.payGrade || "Unknown"}</td>
						</tr>
						<tr>
							<th class="hero-details-th">Date of Entry</th>
							<td>
								{currentHero.dateOfEntry
									? dayjs(currentHero.dateOfEntry).format(
											"MMMM DD, YYYY"
									  )
									: "Unknown"}
							</td>
						</tr>
						<tr>
							<th class="hero-details-th">Date of Discharge</th>
							<td>
								{currentHero.dateOfDischarge
									? dayjs(currentHero.dateOfDischarge).format(
											"MMMM DD, YYYY"
									  )
									: "Unknown"}
							</td>
						</tr>
					</tbody>
				</table>
			</div>
		</div>

		{#if currentHero.bio}
			<div class="hero-bio">
				{@html currentHero.bio}
			</div>
		{/if}
	</svelte:fragment>

	<svelte:fragment slot="footer">
		<button
			class="btn btn-default"
			on:click|preventDefault={modalControls.hide}
		>
			Close
		</button>
	</svelte:fragment>
</Modal>

<style>
	.hero-list {
		display: grid;
		gap: 30px;
		margin-bottom: 30px;
	}

	.hero {
		cursor: pointer;
		margin: 0;
		display: flex;
		flex-flow: column;
		align-items: center;
		padding: 25px;
		position: relative;
		text-align: center;
		transition: all 150ms linear;
	}

	.hero:hover {
		box-shadow: 0 2px 3px 1px rgb(0 0 0 / 30%);
		border-radius: 2px;
		background: #f2f2f2;
	}

	.hero-edit-link {
		background-color: #808080;
		border-radius: 3px;
		color: white;
		display: block;
		height: 25px;
		left: 28px;
		line-height: 26px;
		padding-left: 3px;
		position: absolute;
		text-align: center;
		top: 28px;
		width: 25px;
	}

	.hero-edit-link:hover {
		background-color: #a4a4a4;
	}

	.hero-photo {
		border: 1px solid #333;
		border-radius: 2px;
		object-fit: cover;
		height: 240px !important;
		width: 100%;
	}

	.hero-caption {
		margin-top: calc(25px - (1.42857143em / 2));
	}

	.hero-caption > *:last-child {
		margin-bottom: 0;
	}

	.hero-description:first-child {
		margin-top: 0;
	}

	.hero-description:last-child {
		margin-bottom: 0;
	}

	.hero-service-time {
		font-size: 14px;
	}

	.hero-details-photo {
		text-align: center;
	}

	.hero-details-photo-image {
		width: 100%;
	}

	.hero-details-table {
		margin-bottom: 0;
	}

	.hero-details-photo-image {
		height: 300px !important;
		object-fit: cover;
	}

	.hero-details-th {
		width: 160px;
	}

	.hero-bio > :global(*):last-child {
		margin-bottom: 0;
	}


	@media (min-width: 562px) {
		.hero-list {
			grid-template-columns: 1fr 1fr;
		}
	}

	@media (min-width: 768px) {
		.hero-list {
			grid-template-columns: 1fr 1fr 1fr;
		}

		.hero-details {
			grid-template-columns: 4fr 8fr;
		}
	}

	@media (min-width: 992px) {
		.hero-list {
			grid-template-columns: 1fr 1fr 1fr 1fr;
		}
	}
</style>
